import { Position } from '../../geodesy/geodesy-types';

function jday(date: Date) {
    return date.getTime() / 86400000.0 + 2440587.5;
}

function calculatePositionOfSun(date: Date): [number, number] {
    date = date instanceof Date ? date : new Date();

    const rad = 0.017453292519943295;

    // based on NOAA solar calculations
    const ms_past_midnight =
        ((date.getUTCHours() * 60 + date.getUTCMinutes()) * 60 + date.getUTCSeconds()) * 1000 + date.getUTCMilliseconds();
    const jc = (jday(date) - 2451545) / 36525;
    const mean_long_sun = (280.46646 + jc * (36000.76983 + jc * 0.0003032)) % 360;
    const mean_anom_sun = 357.52911 + jc * (35999.05029 - 0.0001537 * jc);
    const sun_eq =
        Math.sin(rad * mean_anom_sun) * (1.914602 - jc * (0.004817 + 0.000014 * jc)) +
        Math.sin(rad * 2 * mean_anom_sun) * (0.019993 - 0.000101 * jc) +
        Math.sin(rad * 3 * mean_anom_sun) * 0.000289;
    const sun_true_long = mean_long_sun + sun_eq;
    const sun_app_long = sun_true_long - 0.00569 - 0.00478 * Math.sin(rad * 125.04 - 1934.136 * jc);
    const mean_obliq_ecliptic = 23 + (26 + (21.448 - jc * (46.815 + jc * (0.00059 - jc * 0.001813))) / 60) / 60;
    const obliq_corr = mean_obliq_ecliptic + 0.00256 * Math.cos(rad * 125.04 - 1934.136 * jc);

    const lat = Math.asin(Math.sin(rad * obliq_corr) * Math.sin(rad * sun_app_long)) / rad;

    const eccent = 0.016708634 - jc * (0.000042037 + 0.0000001267 * jc);
    const y = Math.tan(rad * (obliq_corr / 2)) * Math.tan(rad * (obliq_corr / 2));
    const rq_of_time =
        4 *
        ((y * Math.sin(2 * rad * mean_long_sun) -
            2 * eccent * Math.sin(rad * mean_anom_sun) +
            4 * eccent * y * Math.sin(rad * mean_anom_sun) * Math.cos(2 * rad * mean_long_sun) -
            0.5 * y * y * Math.sin(4 * rad * mean_long_sun) -
            1.25 * eccent * eccent * Math.sin(2 * rad * mean_anom_sun)) /
            rad);
    const true_solar_time_in_deg = ((ms_past_midnight + rq_of_time * 60000) % 86400000) / 240000;

    const lng = -(true_solar_time_in_deg < 0 ? true_solar_time_in_deg + 180 : true_solar_time_in_deg - 180);

    return [lng, lat];
}

function getAngleBetween(position1: [number, number], position2: [number, number]): number {
    const factor = Math.PI / 180.0;
    const [lon1, lat1] = position1;
    const [lon2, lat2] = position2;

    // Convert degrees to radians
    const aLat = lat1 * factor;
    const aLon = lon1 * factor;
    const bLat = lat2 * factor;
    const bLon = lon2 * factor;

    // Find GC distance
    const centralAngle = Math.acos(Math.sin(aLat) * Math.sin(bLat) + Math.cos(aLat) * Math.cos(bLat) * Math.cos(Math.abs(aLon - bLon)));

    // Convert the central angle from radians to degrees
    return centralAngle / factor;
}

// Shadow level 1 - night, 0 - day
function shadowLevel(position: [number, number], date: Date): number {
    const sunPosition = calculatePositionOfSun(date);
    const angle = getAngleBetween(position, sunPosition);
    let shadowLevel = 0;

    if (angle < 80) {
        shadowLevel = 0;
    } else if (angle > 100) {
        shadowLevel = 1;
    } else {
        shadowLevel = 1 - (100 - angle) / (100 - 80);
    }

    return shadowLevel;
}

export function getShading(departure: Date, arrival: Date, route: [number, Position][]): [Position, number, number][] {
    const totalDistance = route[route.length - 1][0];
    const flightTime = arrival.getTime() - departure.getTime();
    return route.map(([distance, point]) => {
        const time = departure.getTime() + (flightTime * distance) / totalDistance;
        return [point, distance, shadowLevel(point, new Date(time))];
    });
}
